import { useState } from "react"

import { ApiError } from "@/api/client"
import { useEntrar } from "@/api/queries"
import { Logo } from "@/app/logo"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

/**
 * Entrada a la consola.
 *
 * El gateway responde **lo mismo** si el correo no existe que si la contraseña es otra,
 * así que aquí se muestra un único mensaje. Afinarlo —"ese correo no está dado de
 * alta"— convertiría la pantalla de entrada en un buscador de cuentas, y sería la
 * consola deshaciendo desde fuera una decisión que el backend toma a propósito.
 */
export function LoginPage({ onEntrar }: { onEntrar: () => void }) {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const entrar = useEntrar()

  async function enviar(e: React.FormEvent) {
    e.preventDefault()
    try {
      await entrar.mutateAsync({ email: email.trim(), password })
      onEntrar()
    } catch {
      // El error se pinta desde el estado de la mutación; aquí solo se evita que
      // burbujee como rechazo sin capturar.
    }
  }

  return (
    <div className="flex min-h-svh items-center justify-center bg-background p-6">
      <Card className="w-full max-w-sm">
        {/* `justify-items-center` y no `items-center`: `CardHeader` es un GRID, así
            que `items-*` alinea en el eje de bloque y deja el logo pegado a la
            izquierda. Se ve en cuanto se mira, y no se ve nunca si no se mira. */}
        <CardHeader className="justify-items-center gap-3 text-center">
          {/* El logo y no un título de texto: la marca ya dice el nombre, y ponerlo
              dos veces es ruido. La variante clara u oscura la elige el propio
              componente según el tema RESUELTO — sobre el fondo equivocado, media
              marca desaparece. */}
          <Logo className="h-10 w-auto" />
          <CardDescription>Consola de administración del gateway</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={enviar} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Correo</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@empresa.com"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {entrar.isError && (
              <p className="text-sm text-destructive">{mensajeDe(entrar.error)}</p>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={entrar.isPending || email === "" || password === ""}
            >
              {entrar.isPending ? "Entrando…" : "Entrar"}
            </Button>
            <p className="text-xs text-muted-foreground">
              La sesión dura 8 horas y se cierra al cerrar la pestaña.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

function mensajeDe(error: unknown): string {
  if (error instanceof ApiError) {
    // 403 sí se distingue: significa que las credenciales eran correctas pero la
    // cuenta o la organización están desactivadas. Decirle a esa persona "credenciales
    // incorrectas" la manda a buscar una contraseña que no tiene ningún problema.
    if (error.status === 403) return error.detail
    if (error.status === 503) return "La administración no está configurada en este gateway."
  }
  return "Correo o contraseña incorrectos."
}
